import { EmptyStructure } from "./emptyStructure.component";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CreateModal } from "./createModal.component";
import { UnitStructure } from "./unitStructure.component";
import { StructureDropDownRow } from "./structureDropDownRow.component";
import { useUnitState } from "hooks/useUnitState";
import { useOrganizationalStructureStore } from "web-apps/company/stores/organizationalStructureStore/organizationalStructureStore";
import { UnassignedWorkers } from "./unassignedWorkers.component";
import { Text } from "components/Typography/text.styled";
import { ButtonComponent } from "../general/button/button.component";

type Props = {
  entityId: number;
  searchText?: string;
};

export const EntityStructure: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { toggleUnit, isUnitOpen } = useUnitState();
  const [state] = useOrganizationalStructureStore();
  const entity = state.structure.entities.find((e) => e.id === props.entityId);

  const unitMatchesSearch = (unit: any) => {
    if (!props.searchText) return true;

    const lowerSearchText = props.searchText.toLowerCase();

    if (unit.name && unit.name.toLowerCase().includes(lowerSearchText)) {
      return true;
    }

    if (unit.workGroups && Array.isArray(unit.workGroups)) {
      return unit.workGroups.some((workGroup: any) => {
        return (
          workGroup.name &&
          workGroup.name.toLowerCase().includes(lowerSearchText)
        );
      });
    }

    if (unit.teams && Array.isArray(unit.teams)) {
      return unit.teams.some((team: any) => {
        return team.name && team.name.toLowerCase().includes(lowerSearchText);
      });
    }

    return false;
  };

  const filteredUnits = useMemo(() => {
    if (!entity) return [];
    return entity.units.filter(unitMatchesSearch);
  }, [entity?.units, props.searchText]);

  if (!entity) return null;

  return (
    <>
      {filteredUnits.length === 0 && !props.searchText && (
        <EmptyStructure type={"Unit"} id={entity.id} />
      )}
      {filteredUnits.length === 0 && props.searchText && (
        <Text style={{ padding: "20px", textAlign: "center" }}>
          {t("OrganizationalStructure.NoSearchResults", {
            searchText: props.searchText,
          })}
        </Text>
      )}

      {filteredUnits.map((unit) => {
        const isOpen = isUnitOpen(unit.id);

        return (
          <div key={unit.id}>
            <StructureDropDownRow
              type={"unit"}
              data={unit}
              onToggle={() => toggleUnit(unit.id)}
              isOpen={isOpen}
            />
            {isOpen && (
              <UnitStructure unitId={unit.id} searchText={props.searchText} />
            )}
          </div>
        );
      })}

      {entity.units.length > 0 &&
        !props.searchText &&
        entity.name !== "Unhandled" && (
          <ButtonComponent
            size={"small"}
            onClick={() => setShowCreateModal(true)}
            action={true}
            marginBottom={state.unassignedWorkers ? 50 : 200}
            marginLeft={10}
            marginTop={10}
          >
            {t("OrganizationalStructure.CreateUnit")}
          </ButtonComponent>
        )}
      {state.unassignedWorkers && <UnassignedWorkers />}
      <CreateModal
        setShowCreateModal={setShowCreateModal}
        showCreateModal={showCreateModal}
        type={"Unit"}
        id={entity.id}
      />
    </>
  );
};
